/**
 * NUT-09 token restore: scan mints for recoverable Cashu tokens.
 *
 * After a wallet restore from 12 words, this module scans each connected
 * mint for tokens that were created with deterministic secrets (NUT-13).
 * Uses cashu-ts batchRestore to recover proofs and stores them in Evolu.
 *
 * Lives in src/lib/ (not features/) so both onboarding and cashu features
 * can import it without violating the cross-feature import rule.
 *
 * SECURITY: Handles seed material and bearer tokens. Never log proofs or seeds.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module lib/cashu-restore
 */

import { Wallet, CheckStateEnum } from '@cashu/cashu-ts';
import type { Proof } from '@cashu/cashu-ts';
import type { Evolu } from '@evolu/common';
import { NonEmptyString1000, getOrThrow } from '@evolu/common';
import type { SatsflowSchema } from '../db/database';
import { deriveCashuSeed } from './crypto';
import { EvoluCounterSource } from '../features/cashu/utils/counter-source';
import { proofsToEvolu } from '../features/cashu/utils/proof-management';

/** Progress state for a single mint during token restore. */
export interface MintRestoreProgress {
  readonly mintUrl: string;
  readonly mintLabel: string;
  readonly status: 'scanning' | 'complete' | 'error' | 'skipped';
  readonly proofsFound: number;
  readonly satsFound: number;
  readonly error?: string;
}

/** Final result of token restore across all mints. */
export interface TokenRestoreResult {
  readonly totalProofs: number;
  readonly totalSats: number;
  readonly mintResults: MintRestoreProgress[];
}

/** Parameters for restoreTokensFromMints. */
interface RestoreParams {
  readonly mnemonic: string;
  readonly mints: ReadonlyArray<{ url: string; label: string; mintId: string }>;
  readonly evolu: Evolu<SatsflowSchema>;
  readonly onProgress?: (progress: MintRestoreProgress[]) => void;
}

/**
 * Scan connected mints for recoverable Cashu tokens using NUT-09 batchRestore.
 *
 * For each mint:
 * 1. Creates a Wallet with the BIP-39 seed derived from the mnemonic
 * 2. Checks NUT-09 support via mint info
 * 3. Calls batchRestore per active keyset
 * 4. Filters spent tokens via NUT-07
 * 5. Stores recovered proofs in Evolu
 * 6. Advances the counter past the last recovered position
 *
 * Mints are processed sequentially to avoid overwhelming the network.
 * One mint failing does not block others.
 *
 * @param params - Mnemonic, mint list, Evolu instance, and optional progress callback
 * @returns Result with total proofs/sats recovered and per-mint details
 */
export async function restoreTokensFromMints(
  params: RestoreParams,
): Promise<TokenRestoreResult> {
  const { mnemonic, mints, evolu, onProgress } = params;
  const seed = deriveCashuSeed(mnemonic);
  const counterSource = new EvoluCounterSource(evolu);
  const progress: MintRestoreProgress[] = [];

  for (const mint of mints) {
    const mintProgress = await restoreSingleMint({
      mintUrl: mint.url,
      mintLabel: mint.label,
      mintId: mint.mintId,
      seed,
      counterSource,
      evolu,
    });

    progress.push(mintProgress);
    onProgress?.([...progress]);
  }

  const totalProofs = progress.reduce((sum, p) => sum + p.proofsFound, 0);
  const totalSats = progress.reduce((sum, p) => sum + p.satsFound, 0);

  return {
    totalProofs,
    totalSats,
    mintResults: progress,
  };
}

/** Parameters for restoring a single mint. */
interface SingleMintParams {
  readonly mintUrl: string;
  readonly mintLabel: string;
  readonly mintId: string;
  readonly seed: Uint8Array;
  readonly counterSource: EvoluCounterSource;
  readonly evolu: Evolu<SatsflowSchema>;
}

/**
 * Restore tokens from a single mint.
 *
 * @returns Progress result for this mint
 */
async function restoreSingleMint(params: SingleMintParams): Promise<MintRestoreProgress> {
  const { mintUrl, mintLabel, mintId, seed, counterSource, evolu } = params;

  try {
    const wallet = new Wallet(mintUrl, {
      bip39seed: seed,
      counterSource,
    } as ConstructorParameters<typeof Wallet>[1]);

    await wallet.loadMint();

    // Check NUT-09 support
    const mintInfo = wallet.getMintInfo();
    const nut9Support = mintInfo.isSupported(9);
    if (!nut9Support.supported) {
      return {
        mintUrl,
        mintLabel,
        status: 'skipped',
        proofsFound: 0,
        satsFound: 0,
        error: 'Mint does not support token recovery',
      };
    }

    // Get active keysets
    const keysets = wallet.keyChain.getKeysets().filter((ks) => ks.isActive);
    let allProofs: Proof[] = [];

    for (const keyset of keysets) {
      counterSource.setMintUrl(keyset.id, mintUrl);

      const { proofs, lastCounterWithSignature } = await wallet.batchRestore(
        300,
        100,
        0,
        keyset.id,
      );

      if (proofs.length > 0) {
        // NUT-07: filter out spent tokens
        const states = await wallet.checkProofsStates(proofs);
        const unspentProofs = proofs.filter(
          (_, index) => states[index]?.state === CheckStateEnum.UNSPENT,
        );

        allProofs = [...allProofs, ...unspentProofs];
      }

      // Advance counter only when lastCounterWithSignature is defined
      if (lastCounterWithSignature !== undefined) {
        await counterSource.advanceToAtLeast(keyset.id, lastCounterWithSignature + 1);
      }
    }

    // Store recovered proofs in Evolu
    if (allProofs.length > 0) {
      const evoluRows = proofsToEvolu(allProofs, mintId, { type: 'deterministic', counter: 0 });
      // Override derivationCounter to null for batch-restored proofs
      for (const row of evoluRows) {
        const insertRow = {
          mintId: getOrThrow(NonEmptyString1000.from(row.mintId)),
          amount: row.amount as never,
          secret: getOrThrow(NonEmptyString1000.from(row.secret)),
          c: getOrThrow(NonEmptyString1000.from(row.c)),
          keysetId: getOrThrow(NonEmptyString1000.from(row.keysetId)),
          state: getOrThrow(NonEmptyString1000.from(row.state)),
          derivation: getOrThrow(NonEmptyString1000.from('deterministic')),
        };
        evolu.insert('cashuToken', insertRow as never);
      }

      // Create a transaction record for the recovery
      const totalSats = allProofs.reduce((sum, p) => sum + p.amount, 0);
      evolu.insert('transaction', {
        type: getOrThrow(NonEmptyString1000.from('cashu')),
        status: getOrThrow(NonEmptyString1000.from('completed')),
        amountSats: totalSats as never,
        direction: getOrThrow(NonEmptyString1000.from('receive')),
        note: getOrThrow(NonEmptyString1000.from('Recovered via NUT-09 restore')),
        initiatedAt: getOrThrow(NonEmptyString1000.from(new Date().toISOString())),
        settledAt: getOrThrow(NonEmptyString1000.from(new Date().toISOString())),
        custodyType: getOrThrow(NonEmptyString1000.from('cashu')),
        exportedToSatsflow: 0 as never,
        exportedToClams: 0 as never,
      } as never);
    }

    const totalSats = allProofs.reduce((sum, p) => sum + p.amount, 0);

    return {
      mintUrl,
      mintLabel,
      status: 'complete',
      proofsFound: allProofs.length,
      satsFound: totalSats,
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error during token recovery';
    return {
      mintUrl,
      mintLabel,
      status: 'error',
      proofsFound: 0,
      satsFound: 0,
      error: message,
    };
  }
}
